import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartItem } from '../types';
import { Cart } from './Cart';
import { OrderHistory } from './OrderHistory';
import { X, ShoppingCart, History } from 'lucide-react';

interface CartAndHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (uniqueId: string, quantity: number) => void;
  onRemoveItem: (uniqueId: string) => void;
  onCheckout: () => void;
  printSettings: 'manual' | 'auto' | 'none';
  currency: string;
}

export const CartAndHistoryPanel: React.FC<CartAndHistoryPanelProps> = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  printSettings,
  currency,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full shadow-xl flex flex-col animate-slide-in-right">
        {/* En-tête avec onglets et bouton de fermeture */}
        <div className="flex flex-col bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <div className="flex justify-between items-center p-4">
            <h2 className="text-xl font-bold">
              {activeTab === 'current' ? t('Votre panier') : t('Historique des commandes')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
              aria-label={t('Fermer le panneau')}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Onglets */}
          <div className="flex bg-black/10">
            <button
              className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'current'
                  ? 'bg-white/20 text-white border-b-2 border-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              onClick={() => setActiveTab('current')}
            >
              <ShoppingCart size={16} />
              {t('Panier actuel')}
              {cartItems.length > 0 && (
                <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                  {cartItems.length}
                </span>
              )}
            </button>
            <button
              className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'history'
                  ? 'bg-white/20 text-white border-b-2 border-white'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              onClick={() => setActiveTab('history')}
            >
              <History size={16} />
              {t('Historique')}
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-hidden bg-gray-50">
          {activeTab === 'current' ? (
            <Cart
              items={cartItems}
              onUpdateQuantity={onUpdateQuantity}
              onRemoveItem={onRemoveItem}
              onCheckout={onCheckout}
              printSettings={printSettings}
              currency={currency}
            />
          ) : (
            <OrderHistory currency={currency} />
          )}
        </div>
      </div>
    </div>
  );
};